'use client';

import { useTranslation } from '@/hooks/useTranslation';
import BookDemoSection from '@/components/BookDemoSection';
import { FaqAccordion, PageSection, SectionTitle } from '@/components/ui';
import { MetricsBand } from '@/components/primitives/MetricsBand';
import MobilityHero from './MobilityHero';
import IndustryBattleground from '@/components/industry/IndustryBattleground';
import IndustryChallenges from '@/components/industry/IndustryChallenges';
import PlatformCapabilities from '@/components/industry/PlatformCapabilities';
import IndustryResults from '@/components/industry/IndustryResults';
import IndustryCaseStudy from '@/components/industry/IndustryCaseStudy';
import { getIndustryContent } from '@/lib/industryContent';
import '@/components/industry/industry-page.css';

/* Problem-framing stats for the MetricsBand — sourced from J.D. Power 2024,
   Think with Google, and McKinsey 2024 (previously in MobilityHero). */
const METRICS = [
  { value: '9th', label: 'Automotive ranks 9th out of 13 major industries in consumer experience — a persistent gap that costs OEMs loyalty every quarter (J.D. Power 2024)' },
  { value: '76%', label: 'of car buyers research their vehicle online before visiting a dealership — the showroom visit is often the last step, not the first (Think with Google)' },
  { value: '3 in 5', label: 'EV owners cite after-sales service quality as the single biggest factor affecting brand trust in their first year of ownership (McKinsey 2024)' },
];

export default function MobilityBody() {
  const { t } = useTranslation();
  const C = getIndustryContent('mobility');

  const CHALLENGES = [
    {
      title: t("industries_mobility.the_after_sales_gap"),
      desc: t("industries_mobility.delivery_defects_service_centre_del"),
    },
    {
      title: t("industries_mobility.the_ev_trust_problem"),
      desc: t("industries_mobility.ev_after_sales_service_has_a_unique"),
    },
    {
      title: t("industries_mobility.the_launch_window"),
      desc: t("industries_mobility.a_new_model_launch_generates_weeks"),
    },
    {
      title: t("industries_mobility.the_dealer_reputation_gap"),
      desc: t("industries_mobility.a_national_oems_brand_equity_is_onl"),
    },
  ];

  const FAQS = [
    {
      q: t("industries_mobility.what_is_an_automotive_customer_expe"),
      a: t("industries_mobility.one_workspace_for_after_sales_compl"),
    },
    {
      q: t("industries_mobility.how_does_locobuzz_help_automobile_b"),
      a: t("industries_mobility.all_after_sales_complaint_channels"),
    },
    {
      q: t("industries_mobility.can_locobuzz_help_ev_brands_get_ahe"),
      a: t("industries_mobility.yes_dedicated_ev_monitoring_for_ran"),
    },
    {
      q: t("industries_mobility.how_does_locobuzz_track_social_medi"),
      a: t("industries_mobility.launch_intelligence_dashboard_track"),
    },
    {
      q: t("industries_mobility.can_locobuzz_manage_reputation_and"),
      a: t("industries_mobility.yes_dealer_level_and_network_level"),
    },
    {
      q: t("industries_mobility.does_locobuzz_monitor_automotive_co"),
      a: t("industries_mobility.yes_automotive_forum_monitoring_you"),
    },
  ];

  const BG_TITLE = t('industries_mobility.why_is_aftersales_complaint_managem');
  const BG_ACCENT = 'Automotive OEMs?';
  const _bgCut = BG_TITLE.lastIndexOf(BG_ACCENT);
  const bgTitle = _bgCut > 0 ? BG_TITLE.slice(0, _bgCut).trim() : BG_TITLE;
  const bgAccent = _bgCut > 0 ? BG_TITLE.slice(_bgCut) : undefined;

  return (
    <div className="industry-page-wrap">
      <span className="sr-only">{t("industries_mobility.locobuzz_is_an_ainative_customer_ex")}</span>
      <span className="sr-only">{t("industries_mobility.the_automotive_sector_consistently")}</span>
      <span className="sr-only">{t("industries_mobility.locobuzz_maps_six_purposebuilt_capa")}</span>
      <span className="sr-only">{t("industries_mobility.automotive_brands_using_locobuzz_re")}</span>

      <MobilityHero />

      <MetricsBand items={METRICS} columns={3} />

      <IndustryBattleground
        eyebrow={t("industries_mobility.why_aftersales_cx_is_critical_for_a")}
        title={bgTitle}
        titleAccent={bgAccent}
        paragraph={t("industries_mobility.when_a_customer_posts_about_a_warra")}
        imageSrc="/industries/why/mobility.png"
        imageAlt={t("industries_mobility.automotive_brand_shareofvoice_and_c")}
        bg="tint"
      />

      <IndustryChallenges
        title={t("industries_mobility.what_cx_challenges_do_automotive_oe")}
        items={CHALLENGES}
        align="center"
        bg="page"
      />

      <PlatformCapabilities {...C.capabilities} />

      <IndustryResults {...C.results} />

      <IndustryCaseStudy {...C.caseStudy} />

      <PageSection id="faqs" bg="tint">
        <SectionTitle
          title={
            <>
              {t('faq.everything_you_need_to')}{' '}
              <span className="serif-accent italic">{t('faq.know')}</span>
            </>
          }
          align="center"
        />
        <FaqAccordion items={FAQS} columns={2} name="mobility-faq" />
      </PageSection>

      <BookDemoSection />
    </div>
  );
}
